import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { toast } from "react-toastify";

import style from "./tenants.scss";
import AlertDialog from "components/alert-dialog/AlertDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { CombinedTier, LicensingModel, TenantListItem, TenantType } from "domain/tenants";
import { tenantService } from "services/tenants/TenantService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

export interface MoveTenantDialogProperties {
    displayMoveTenantModal: boolean;
    setDisplayMoveTenantModal: (display: boolean) => void;
    introductionMessage: string | JSX.Element;
    title: string;
    loading: boolean;
    tenants: TenantListItem[];
    currentTenantId: string;
    customerName: string;
    currentTenantTier: CombinedTier;
    currentTenantType: TenantType;
    currentTenantLicensingModel: LicensingModel;
    tenantUuid: string;
    onRefresh: () => void;
}

const MoveTenantDialog = (props: MoveTenantDialogProperties & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [selectedTenant, setSelectedTenant] = React.useState("");
    const [searchTerm, setSearchTerm] = React.useState("");
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [showTierMismatchAlert, setShowTierMismatchAlert] = React.useState(false);
    const [apiTenants, setApiTenants] = React.useState<TenantListItem[]>([]);
    const [apiLoading, setApiLoading] = React.useState(false);
    const [moveLoading, setMoveLoading] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const filteredTenants = React.useMemo(
        () =>
            apiTenants.filter(
                (tenant) =>
                    tenant.uuid !== props.currentTenantId &&
                    tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
            ),
        [apiTenants, props.currentTenantId, searchTerm]
    );

    const resetState = React.useCallback(() => {
        setSelectedTenant("");
        setSearchTerm("");
        setIsDropdownOpen(false);
        setShowConfirmation(false);
        setShowTierMismatchAlert(false);
    }, []);

    const closeModal = React.useCallback(() => {
        resetState();
        props.setDisplayMoveTenantModal(false);
    }, [resetState, props]);

    const refreshPage = React.useCallback(() => {
        props.onRefresh();
    }, [props.onRefresh]);

    const moveTenant = React.useCallback(async () => {
        setMoveLoading(true);
        try {
            await tenantService.moveTenant(props.tenantUuid, selectedTenant);
            setMoveLoading(false);
            closeModal();
            toast(
                <SuccessIcon
                    successClass={layoutStyle.customToastSuccessIcon}
                    color={props.theme.contentBackgroundColor}
                    text={t("EditCustomerView.move.successMessage")}
                />,
                {
                    closeButton: (closeToastProps) => (
                        <CustomToastCloseButton
                            closeToast={{
                                ...closeToastProps,
                                closeToast: (e) => (closeToastProps.closeToast(e), refreshPage()),
                            }}
                            color={props.theme.iconFillColor}
                        />
                    ),
                    className: layoutStyle.customToastSuccessMessage,
                    onClose: refreshPage,
                }
            );
        } catch (error) {
            setMoveLoading(false);
            console.error("Move tenant failed:", error);
        }
    }, [props.tenantUuid, selectedTenant, props.theme, t, closeModal, refreshPage]);

    React.useEffect(() => {
        if (!props.displayMoveTenantModal) {
            resetState();
        }
    }, [props.displayMoveTenantModal, resetState]);

    React.useEffect(() => {
        if (props.displayMoveTenantModal && props.tenantUuid) {
            setApiLoading(true);
            const abortController = new AbortController();
            tenantService
                .fetchMovableTenants(props.tenantUuid, abortController)
                .then(setApiTenants)
                .finally(() => setApiLoading(false));
            return () => abortController.abort();
        }
    }, [props.displayMoveTenantModal, props.tenantUuid]);

    function onOldLicensingModel(licensingModel: LicensingModel) {
        return licensingModel === LicensingModel.OLD_MODEL;
    }

    function onConflictingModels(newParentModel: LicensingModel) {
        return props.currentTenantLicensingModel !== newParentModel;
    }

    const handleMoveConfirmation = () => {
        const selectedParent = apiTenants.find((t) => t.uuid === selectedTenant);
        if (!selectedParent) {
            return;
        }
        if (
            selectedParent.type === "INTERNAL" ||
            (selectedParent.type === "CHANNEL" && !onConflictingModels(selectedParent.licensingModel)) ||
            (onOldLicensingModel(props.currentTenantLicensingModel) &&
                onOldLicensingModel(selectedParent.licensingModel)) ||
            selectedParent.tier === props.currentTenantTier
        ) {
            setShowConfirmation(false);
            moveTenant();
            return;
        }
        setShowConfirmation(false);
        setShowTierMismatchAlert(true);
    };

    const handleTierMismatchConfirm = () => {
        setShowTierMismatchAlert(false);
        setShowConfirmation(false);
        moveTenant();
    };

    return (
        <Modal isOpen={props.displayMoveTenantModal} hideModal={closeModal} modalTitle={props.title}>
            {props.loading || apiLoading || moveLoading ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div
                        className={classNames(form.deleteContainer, style.moveTenantSearch)}
                        data-testid={testIds.workArea.tenant.moveDialog.itself}
                    >
                        {props.introductionMessage}
                        <div
                            ref={dropdownRef}
                            className={style.tenantSelector}
                            onBlur={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    setIsDropdownOpen(false);
                                    setSearchTerm("");
                                }
                            }}
                            tabIndex={-1}
                        >
                            <select
                                id={"tenantSelector"}
                                value={selectedTenant}
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className={classNames(form.select, form.fixedWidthInput)}
                                data-testid={testIds.workArea.tenant.moveDialog.tenantSelect}
                            >
                                <option value="">
                                    {selectedTenant
                                        ? apiTenants.find((t) => t.uuid === selectedTenant)?.name
                                        : t("EditCustomerView.move.selectTenant")}
                                </option>
                            </select>
                            {isDropdownOpen && (
                                <div
                                    className={style.tenantDropdown}
                                    data-testid={testIds.common.searchSelectContainer.itself}
                                >
                                    <div className={style.searchWrapper}>
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder={t("Common.search")}
                                            data-testid={testIds.common.searchSelectContainer.searchInput.itself}
                                            className={style.searchInput}
                                            autoFocus
                                        />
                                    </div>
                                    <div
                                        className={style.optionsContainer}
                                        data-testid={testIds.common.searchSelectContainer.optionContainer}
                                    >
                                        {filteredTenants.map((tenant) => (
                                            <div
                                                key={tenant.uuid}
                                                onClick={() => (
                                                    setSelectedTenant(tenant.uuid),
                                                    setSearchTerm(""),
                                                    setIsDropdownOpen(false)
                                                )}
                                                className={style.selectedTenant}
                                            >
                                                {tenant.name}
                                            </div>
                                        ))}
                                        {filteredTenants.length === 0 && (
                                            <div className={style.noResults}>
                                                <p>{t("EditCustomerView.move.tenantNotFound")}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {selectedTenant &&
                            (() => {
                                const selectedTenantData = apiTenants.find((t) => t.uuid === selectedTenant);
                                return (
                                    selectedTenantData?.combinedDepthExceeded && (
                                        <div className={style.errorBanner}>
                                            <div className={style.warningIcon} tabIndex={0}>
                                                <FailedRedNotificationIcon
                                                    backgroundColor={props.theme.bannerIconBackgroundColor}
                                                    iconColor={props.theme.bannerIconColor}
                                                />
                                            </div>
                                            <p>{t("EditCustomerView.move.errorBanner")}</p>
                                        </div>
                                    )
                                );
                            })()}

                        <div className={buttons.buttonContainer}>
                            <button
                                className={classNames(buttons.primaryButton, buttons.medium, buttons.buttonWithoutIcon)}
                                data-testid={testIds.workArea.tenant.moveDialog.moveButton}
                                disabled={
                                    !selectedTenant ||
                                    apiTenants.find((t) => t.uuid === selectedTenant)?.combinedDepthExceeded
                                }
                                onClick={() => setShowConfirmation(true)}
                            >
                                {t("EditCustomerView.move.move")}
                            </button>
                            <button
                                className={buttons.secondaryButtonWithoutIcon}
                                onClick={closeModal}
                                data-testid={testIds.common.dialog.closeButton}
                            >
                                {t("Common.cancel")}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {showConfirmation && (
                <Modal
                    isOpen={showConfirmation}
                    hideModal={() => setShowConfirmation(false)}
                    modalTitle={t("EditCustomerView.move.confirmationTitle", {
                        tenantName: apiTenants.find((t) => t.uuid === selectedTenant)?.name,
                    })}
                >
                    <div className={form.deleteContainer}>
                        <p>
                            {t("EditCustomerView.move.confirmMessage", {
                                moveTenant: props.customerName,
                                toParent: apiTenants.find((t) => t.uuid === selectedTenant)?.name,
                            })}
                        </p>

                        <div className={classNames(buttons.buttonContainer, style.confirmationButtonContainer)}>
                            <button
                                className={classNames(buttons.primaryButton, buttons.medium, buttons.buttonWithoutIcon)}
                                data-testid={testIds.common.confirmationDialog.confirmButton}
                                onClick={handleMoveConfirmation}
                            >
                                {t("EditCustomerView.move.confirmation")}
                            </button>
                            <button
                                className={buttons.secondaryButtonWithoutIcon}
                                data-testid={testIds.common.dialog.closeButton}
                                onClick={closeModal}
                            >
                                {t("Common.cancel")}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showTierMismatchAlert && (
                <AlertDialog
                    isOpen={showTierMismatchAlert}
                    hideModal={() => setShowTierMismatchAlert(false)}
                    headingText={t("EditCustomerView.move.moveTenant")}
                    primaryButton={{
                        text: t("EditCustomerView.move.finalConfirm"),
                        action: () => {
                            handleTierMismatchConfirm();
                        },
                    }}
                    closeButton={{
                        action: closeModal,
                        text: t("Common.cancel"),
                    }}
                    type={"WARNING"}
                    body={t("EditCustomerView.move.licenseMismatchMessage", {
                        moveTenant: props.customerName,
                        toParent: apiTenants.find((t) => t.uuid === selectedTenant)?.name,
                    })}
                />
            )}
        </Modal>
    );
};

export default connector(MoveTenantDialog);
